/**
 * 提示和通知上下文管理
 *
 * 这个文件负责管理应用程序的全局提示和通知系统，包括：
 * - 错误提示管理
 * - 成功提示管理
 * - 通知消息管理
 * - 通知中心管理
 */

import cloneDeep from 'lodash-es/cloneDeep'
import uniqueId from 'lodash-es/uniqueId'
import { createContext, ReactNode, useState } from 'react'
import { AlertItemType } from '../types/alerts'

/**
 * 提示上下文类型定义
 */
type alertContextType = {
  /** 错误数据 */
  errorData: { title: string; list?: Array<string> }
  /** 设置错误数据 */
  setErrorData: (newState: { title: string; list?: Array<string> }) => void
  /** 错误提示是否打开 */
  errorOpen: boolean
  /** 设置错误提示打开状态 */
  setErrorOpen: (newState: boolean) => void
  /** 通知数据 */
  noticeData: { title: string; link?: string }
  /** 设置通知数据 */
  setNoticeData: (newState: { title: string; link?: string }) => void
  /** 通知是否打开 */
  noticeOpen: boolean
  /** 设置通知打开状态 */
  setNoticeOpen: (newState: boolean) => void
  /** 成功数据 */
  successData: { title: string }
  /** 设置成功数据 */
  setSuccessData: (newState: { title: string }) => void
  /** 成功提示是否打开 */
  successOpen: boolean
  /** 设置成功提示打开状态 */
  setSuccessOpen: (newState: boolean) => void
  /** 通知中心是否打开 */
  notificationCenter: boolean
  /** 设置通知中心打开状态 */
  setNotificationCenter: (newState: boolean) => void
  /** 通知列表 */
  notificationList: Array<AlertItemType>
  /** 添加通知到列表 */
  pushNotificationList: (Object: AlertItemType) => void
  /** 清空通知列表 */
  clearNotificationList: () => void
  /** 从通知列表中移除指定通知 */
  removeFromNotificationList: (index: string) => void
}

/**
 * 提示上下文的初始值
 */
const initialValue: alertContextType = {
  errorData: { title: '', list: [] },
  setErrorData: () => {},
  errorOpen: false,
  setErrorOpen: () => {},
  noticeData: { title: '', link: '' },
  setNoticeData: () => {},
  noticeOpen: false,
  setNoticeOpen: () => {},
  successData: { title: '' },
  setSuccessData: () => {},
  successOpen: false,
  setSuccessOpen: () => {},
  notificationCenter: false,
  setNotificationCenter: () => {},
  notificationList: [],
  pushNotificationList: () => {},
  clearNotificationList: () => {},
  removeFromNotificationList: () => {}
}

// 创建提示上下文
export const alertContext = createContext<alertContextType>(initialValue)

/**
 * 提示提供者组件
 *
 * @param children - 子组件
 * @returns 提供提示和通知相关状态和方法的上下文提供者
 */
export function AlertProvider({ children }: { children: ReactNode }) {
  const [errorData, setErrorDataState] = useState<{
    title: string
    list?: Array<string>
  }>({ title: '', list: [] })
  const [errorOpen, setErrorOpen] = useState(false)
  const [noticeData, setNoticeDataState] = useState<{
    title: string
    link?: string
  }>({ title: '', link: '' })
  const [noticeOpen, setNoticeOpen] = useState(false)
  const [successData, setSuccessDataState] = useState<{ title: string }>({
    title: ''
  })
  const [successOpen, setSuccessOpen] = useState(false)
  const [notificationCenter, setNotificationCenter] = useState(false)
  const [notificationList, setNotificationList] = useState([])

  /**
   * 添加通知到列表
   * @param notification - 要添加的通知对象
   */
  const pushNotificationList = (notification: AlertItemType) => {
    setNotificationList(old => {
      const newNotificationList = cloneDeep(old)
      newNotificationList.unshift(notification)
      return newNotificationList
    })
  }
  /**
   * 设置错误数据，打开错误对话框并将新的错误通知添加到通知列表
   * @param newState - 包含新错误数据的对象，包括标题和可选的错误消息列表
   */
  function setErrorData(newState: { title: string; list?: Array<string> }) {
    setErrorDataState(newState)
    setErrorOpen(true)
    setNotificationCenter(true)
    pushNotificationList({
      type: 'error',
      title: newState.title || ' ',
      list: newState.list,
      id: uniqueId()
    })
  }

  /**
   * 设置通知数据状态并打开通知模态框，如果标题已定义，还会将新通知添加到通知中心
   * @param newState - 包含通知标题和可选链接的对象
   */
  function setNoticeData(newState: { title: string; link?: string }) {
    if (newState.title && newState.title !== '') {
      setNoticeDataState(newState)
      setNoticeOpen(true)
      // 将新通知添加到通知中心
      setNotificationCenter(true)
      pushNotificationList({
        type: 'notice',
        title: newState.title,
        link: newState.link,
        id: uniqueId()
      })
    }
  }

  /**
   * 更新成功数据状态并显示成功提示通知
   * @param newState - 包含要设置的成功数据状态的"title"属性的状态对象
   */
  function setSuccessData(newState: { title: string }) {
    // 如果新状态有"title"属性，将新的成功通知添加到列表
    if (newState.title && newState.title !== '') {
      setSuccessDataState(newState) // 使用提供的新状态更新成功数据状态
      setSuccessOpen(true) // 打开成功提示
      setNotificationCenter(true) // 显示通知中心
      pushNotificationList({
        // 将新通知添加到列表
        type: 'success',
        title: newState.title,
        id: uniqueId()
      })
    }
  }

  /**
   * 清空通知列表
   */
  function clearNotificationList() {
    setNotificationList([])
  }

  /**
   * 从通知列表中移除指定通知
   * @param index - 要移除的通知ID
   */
  function removeFromNotificationList(index: string) {
    // 将通知列表设置为过滤掉匹配ID的提示的新数组
    setNotificationList(prevAlertsList => prevAlertsList.filter(alert => alert.id !== index))
  }
  return (
    <alertContext.Provider
      value={{
        removeFromNotificationList,
        clearNotificationList,
        notificationList,
        pushNotificationList,
        setNotificationCenter,
        notificationCenter,
        errorData,
        setErrorData,
        errorOpen,
        setErrorOpen,
        noticeData,
        setNoticeData,
        noticeOpen,
        setNoticeOpen,
        successData,
        setSuccessData,
        successOpen,
        setSuccessOpen
      }}
    >
      {children}
    </alertContext.Provider>
  )
}
